import { Chip } from '@monorepo/shared/componentsV2/Chip';
import { bodyRegular } from '@monorepo/shared/styles/text';
import DataSetupIcon from '@svg/data-setup.svg';
import DatabaseIcon from '@svg/database.svg';
import TableIcon from '@svg/table.svg';
import TreeStructureIcon from '@svg/tree-structure.svg';
import { DatasetType } from 'mapistry-shared';
import React from 'react';
import styled, { DefaultTheme } from 'styled-components';

function getBackgroundColor({
  theme,
  $type,
}: {
  theme: DefaultTheme;
  $type: DatasetType | undefined;
}) {
  if ($type === DatasetType.LOG) {
    return theme.colors.logPurple;
  }
  if ($type === DatasetType.RESOURCE_TYPE) {
    return theme.colors.resourceGray;
  }
  if ($type === DatasetType.WORKFLOW) {
    return theme.colors.workflowBlue;
  }
  return theme.colors.blue;
}

const StyledChip = styled(Chip)<{ $type: DatasetType | undefined }>`
  height: auto;
  padding: 0 0.25rem 0 0;
  color: ${({ theme }) => theme.colors.grayeee};
  background-color: ${({ theme, $type }) =>
    getBackgroundColor({ theme, $type })};

  .chip-label {
    padding-left: 0;
  }
`;

const StyledWhiteIcon = styled.svg`
  fill: white;
`;

const LabelWithIcon = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: start;
  ${bodyRegular};
  max-width: 100%;
`;

const IconContainer = styled.div<{ $type: DatasetType | undefined }>`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  padding: 0.35rem;
  margin-right: 0.5rem;
  background-color: ${getBackgroundColor};
  border-radius: 5px;

  & svg {
    flex-shrink: 0;
    width: 0.9rem;
    height: 0.9rem;
    fill: ${({ theme }) => theme.colors.grayeee};
  }
`;

const Label = styled.div`
  max-width: 100%;
  padding: 0.3rem 0;
  text-align: start;
  white-space: normal;
`;

function DatasetIcon({ type }: { type: DatasetType | undefined }) {
  switch (type) {
    case DatasetType.RESOURCE_TYPE:
      return <StyledWhiteIcon as={DatabaseIcon} />;
    case DatasetType.LOG:
      return <StyledWhiteIcon as={TableIcon} />;
    case DatasetType.WORKFLOW:
    case DatasetType.AGGREGATION_STEP:
    case DatasetType.FORMULA_STEP:
    case DatasetType.JOIN_STEP:
      return <StyledWhiteIcon as={TreeStructureIcon} />;
    case undefined:
      return <StyledWhiteIcon as={DataSetupIcon} />;
    default: {
      // we don't expect to ever show a dataset chip for an analytics sync dataset
      const exhaustive: never | DatasetType.ANALYTICS_SYNC = type;
      throw new Error(`Invalid dataset type - ${exhaustive}`);
    }
  }
}

interface DatasetChipProps {
  className?: string;
  labelClassName?: string;
  name: string;
  type: DatasetType | undefined;
}

export function DatasetChip({
  className,
  labelClassName,
  name,
  type,
}: DatasetChipProps) {
  const label = (
    <LabelWithIcon>
      <IconContainer aria-hidden $type={type}>
        <DatasetIcon type={type} />
      </IconContainer>
      <Label className={labelClassName}>{name}</Label>
    </LabelWithIcon>
  );

  return <StyledChip className={className} label={label} $type={type} />;
}
